<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { useToast } from 'primevue/usetoast'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Button from 'primevue/button'
import Card from 'primevue/card'
import Tag from 'primevue/tag'
import Select from 'primevue/select'
import axiosInstance from '@/plugins/axios'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

interface Module {
  _id: string
  key: string
  name: string
  description: string
  enabledByDefault: boolean
  released: boolean
  createdAt?: string
  updatedAt?: string
}

interface TenantInfo {
  _id: string
  name: string
}

interface TenantModuleInfo {
  _id: string
  key: string
  name: string
  description: string
  released: boolean
  tenantState: {
    status: string
    lockedByAdmin: boolean
    settings: Record<string, any>
    validationState: any
    activatedAt?: string
    liveAt?: string
  } | null
}

const toast = useToast()

// Global module management
const modules = ref<Module[]>([])
const loading = ref(false)
const updating = ref<string | null>(null)

// Per-tenant module management
const tenants = ref<TenantInfo[]>([])
const selectedTenant = ref<TenantInfo | null>(null)
const tenantModules = ref<TenantModuleInfo[]>([])
const tenantLoading = ref(false)
const tenantUpdating = ref<string | null>(null)

const loadModules = async () => {
  loading.value = true
  try {
    const response = await axiosInstance.get('/modules')
    modules.value = response.data
  } catch (error) {
    console.error('Error loading modules:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Modules could not be loaded',
      life: 3000
    })
  } finally {
    loading.value = false
  }
}

const toggleRelease = async (module: Module) => {
  updating.value = module._id
  const action = module.released ? 'unreleased' : 'release'

  try {
    await axiosInstance.post(`/modules/${module._id}/${action}`)
    module.released = !module.released

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: module.released
        ? `Module "${module.name}" has been released`
        : `Module "${module.name}" has been locked`,
      life: 3000
    })
  } catch (error) {
    console.error('Error toggling release:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Status could not be changed',
      life: 3000
    })
  } finally {
    updating.value = null
  }
}

// --- Per-Tenant Management ---

const loadTenants = async () => {
  try {
    const response = await axiosInstance.get('/admin/getAllTenants')
    tenants.value = response.data
  } catch (error) {
    console.error('Error loading tenants:', error)
  }
}

const loadTenantModules = async () => {
  if (!selectedTenant.value) return
  tenantLoading.value = true
  try {
    const response = await axiosInstance.get(
      `/modules/admin/tenant/${selectedTenant.value._id}/full`
    )
    tenantModules.value = response.data
  } catch (error) {
    console.error('Error loading tenant modules:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Could not load tenant modules',
      life: 3000
    })
  } finally {
    tenantLoading.value = false
  }
}

const enableModuleForTenant = async (moduleKey: string) => {
  if (!selectedTenant.value) return
  tenantUpdating.value = moduleKey
  try {
    await axiosInstance.post(
      `/modules/admin/tenant/${selectedTenant.value._id}/enable/${moduleKey}`
    )
    toast.add({
      severity: 'success',
      summary: 'Enabled',
      detail: 'Module has been enabled for tenant',
      life: 3000
    })
    await loadTenantModules()
  } catch (error: any) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: error.response?.data?.message || 'Error',
      life: 3000
    })
  } finally {
    tenantUpdating.value = null
  }
}

const toggleLockForTenant = async (module: TenantModuleInfo) => {
  if (!selectedTenant.value || !module.tenantState) return
  tenantUpdating.value = module.key
  const action = module.tenantState.lockedByAdmin ? 'unlock' : 'lock'
  try {
    await axiosInstance.post(
      `/modules/admin/tenant/${selectedTenant.value._id}/${action}/${module.key}`
    )
    toast.add({
      severity: 'success',
      summary: action === 'lock' ? 'Locked' : 'Unlocked',
      detail: `Module has been ${action === 'lock' ? 'locked' : 'unlocked'} for tenant`,
      life: 3000
    })
    await loadTenantModules()
  } catch (error: any) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: error.response?.data?.message || 'Error',
      life: 3000
    })
  } finally {
    tenantUpdating.value = null
  }
}

const deactivateModuleForTenant = async (moduleKey: string) => {
  if (!selectedTenant.value) return
  tenantUpdating.value = moduleKey
  try {
    await axiosInstance.post(
      `/modules/admin/tenant/${selectedTenant.value._id}/deactivate/${moduleKey}`
    )
    toast.add({
      severity: 'info',
      summary: 'Deactivated',
      detail: 'Module has been deactivated for tenant',
      life: 3000
    })
    await loadTenantModules()
  } catch (error: any) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: error.response?.data?.message || 'Error',
      life: 3000
    })
  } finally {
    tenantUpdating.value = null
  }
}

const getTenantStatusLabel = (module: TenantModuleInfo): string => {
  if (!module.tenantState) return 'Not enabled'
  if (module.tenantState.lockedByAdmin) return 'Locked'
  return module.tenantState.status.charAt(0).toUpperCase() + module.tenantState.status.slice(1)
}

const getTenantStatusSeverity = (module: TenantModuleInfo): string => {
  if (!module.tenantState) return 'secondary'
  if (module.tenantState.lockedByAdmin) return 'danger'
  switch (module.tenantState.status) {
    case 'live':
      return 'success'
    case 'configuring':
      return 'warn'
    default:
      return 'secondary'
  }
}

watch(selectedTenant, () => {
  if (selectedTenant.value) {
    loadTenantModules()
  } else {
    tenantModules.value = []
  }
})

onMounted(() => {
  loadModules()
  loadTenants()
})
</script>

<template>
  <div class="modules-container">
    <!-- Global Module Management -->
    <Card>
      <template #title>Global Module Release</template>
      <template #content>
        <DataTable
          :value="modules"
          :loading="loading"
          stripedRows
          :paginator="true"
          :rows="10"
          responsiveLayout="scroll"
        >
          <Column field="key" header="Key" sortable>
            <template #body="{ data }">
              <code>{{ data.key }}</code>
            </template>
          </Column>
          <Column field="name" header="Name" sortable />
          <Column field="description" header="Description" />
          <Column field="enabledByDefault" header="Default" sortable>
            <template #body="{ data }">
              <Tag
                :value="data.enabledByDefault ? 'Active' : 'Inactive'"
                :severity="data.enabledByDefault ? 'success' : 'secondary'"
              />
            </template>
          </Column>
          <Column field="released" header="Status" sortable>
            <template #body="{ data }">
              <Tag
                :value="data.released ? 'Released' : 'Locked'"
                :severity="data.released ? 'success' : 'danger'"
              />
            </template>
          </Column>
          <Column header="Action" style="width: 150px">
            <template #body="{ data }">
              <Button
                :label="data.released ? 'Lock' : 'Release'"
                :severity="data.released ? 'danger' : 'success'"
                size="small"
                @click="toggleRelease(data)"
                :disabled="updating === data._id"
              >
                <template #icon>
                  <FontAwesomeIcon v-if="updating === data._id" icon="spinner" spin />
                  <FontAwesomeIcon v-else :icon="data.released ? 'lock' : 'lock-open'" />
                </template>
              </Button>
            </template>
          </Column>
        </DataTable>
      </template>
    </Card>

    <!-- Per-Tenant Module Management -->
    <Card class="mt-4">
      <template #title>Per-Tenant Module Management</template>
      <template #content>
        <div class="mb-3">
          <label for="tenant-select" class="block mb-2 font-semibold">Select Tenant</label>
          <Select
            id="tenant-select"
            v-model="selectedTenant"
            :options="tenants"
            optionLabel="name"
            placeholder="Select a tenant..."
            class="w-full md:w-20rem"
            filter
          />
        </div>

        <DataTable
          v-if="selectedTenant"
          :value="tenantModules"
          :loading="tenantLoading"
          stripedRows
          :paginator="true"
          :rows="10"
          responsiveLayout="scroll"
        >
          <Column field="key" header="Key" sortable>
            <template #body="{ data }">
              <code>{{ data.key }}</code>
            </template>
          </Column>
          <Column field="name" header="Name" sortable />
          <Column header="Released" sortable field="released">
            <template #body="{ data }">
              <Tag
                :value="data.released ? 'Released' : 'Not Released'"
                :severity="data.released ? 'success' : 'secondary'"
              />
            </template>
          </Column>
          <Column header="Tenant Status">
            <template #body="{ data }">
              <Tag
                :value="getTenantStatusLabel(data)"
                :severity="getTenantStatusSeverity(data)"
                :icon="data.tenantState?.lockedByAdmin ? 'pi pi-lock' : undefined"
              />
            </template>
          </Column>
          <Column header="Action" style="width: 280px">
            <template #body="{ data }">
              <div class="flex gap-2">
                <!-- Not enabled yet: show Enable button -->
                <Button
                  v-if="!data.tenantState"
                  label="Enable"
                  icon="pi pi-plus"
                  severity="success"
                  size="small"
                  @click="enableModuleForTenant(data.key)"
                  :disabled="tenantUpdating === data.key"
                  :loading="tenantUpdating === data.key"
                />
                <!-- Enabled: show Lock/Unlock + Deactivate -->
                <template v-else>
                  <Button
                    :label="data.tenantState.lockedByAdmin ? 'Unlock' : 'Lock'"
                    :icon="
                      tenantUpdating === data.key
                        ? 'pi pi-spin pi-spinner'
                        : data.tenantState.lockedByAdmin
                          ? 'pi pi-lock-open'
                          : 'pi pi-lock'
                    "
                    :severity="data.tenantState.lockedByAdmin ? 'success' : 'danger'"
                    size="small"
                    @click="toggleLockForTenant(data)"
                    :disabled="tenantUpdating === data.key"
                  />
                  <Button
                    v-if="data.tenantState.status !== 'inactive'"
                    label="Deactivate"
                    icon="pi pi-power-off"
                    severity="warn"
                    size="small"
                    outlined
                    @click="deactivateModuleForTenant(data.key)"
                    :disabled="tenantUpdating === data.key"
                  />
                </template>
              </div>
            </template>
          </Column>
        </DataTable>

        <div v-if="!selectedTenant" class="text-center py-4">
          <i class="pi pi-info-circle text-2xl text-gray-400 mb-2"></i>
          <p class="text-gray-500">Select a tenant to manage module access</p>
        </div>
      </template>
    </Card>
  </div>
</template>

<style scoped>
.modules-container {
  padding: 1.5rem;
}

code {
  background-color: #f4f4f4;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-family: monospace;
}
</style>
